import React, { useEffect, useState } from "react";
import { getLmsCombinedScores, getAllEmployees } from "../api/client";

/**
 * Combined-score widget for the AI Dashboard.
 *
 * What it does:
 *  - Lets the admin pick any employee (typeahead by name/email)
 *  - Calls /api/lms/scores/{user_id}
 *  - Shows four stat cards: White, Yellow, Assessment, Average-of-three
 *  - Each cell renders "—" when the source is missing, instead of a 0%
 *    that would mislead anyone into thinking the user scored zero
 *
 * Why a separate widget:
 *  - Moodle calls are slow (1-3 seconds for the full chain) and not every
 *    dashboard view needs them. Wrapping the call behind a manual "Check"
 *    keeps the rest of the dashboard fast.
 *  - The bulk `/api/lms/scores?user_ids=...` endpoint exists for a future
 *    table view; this card is the simple "look up one person" UX.
 */
export default function CombinedScoreCard() {
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [scores, setScores] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAllEmployees();
        setEmployees((res.data || []).filter((e) => e.user_id));
      } catch (e) {
        // Non-admin users won't have access to /api/admin/employees;
        // they'd just see an empty selector. Safe to swallow.
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    load();
  }, []);

  const handleCheck = async () => {
    if (!selectedUserId || busy) return;
    setBusy(true);
    setError(null);
    setScores(null);
    try {
      const res = await getLmsCombinedScores(selectedUserId);
      setScores(res.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        setError("LMS not configured on this environment.");
      } else {
        setError(e?.response?.data?.detail || "Failed to fetch combined scores");
      }
    } finally {
      setBusy(false);
    }
  };

  const fmtPct = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);
  const colorFor = (v) => {
    if (v == null) return "var(--text-muted)";
    if (v >= 70) return "var(--color-correct)";
    if (v >= 50) return "#eab308";
    return "var(--color-incorrect)";
  };

  return (
    <div
      className="aiq-card"
      style={{
        marginBottom: 24,
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Combined Score (LMS + Assessment)
          </h2>
          <p
            className="aiq-muted"
            style={{ fontSize: 12, margin: "4px 0 0 0" }}
          >
            White Belt + Yellow Belt grades pulled from Moodle, averaged with
            the candidate's best AIQ assessment score.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 320px", minWidth: 200 }}>
          <label htmlFor="combined-emp" className="aiq-label">
            Employee
          </label>
          <select
            id="combined-emp"
            className="aiq-select"
            disabled={employeesLoading}
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <option value="">
              {employeesLoading ? "Loading employees…" : "Select an employee…"}
            </option>
            {[...employees]
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((e) => (
                <option key={e.user_id} value={e.user_id}>
                  {e.name} — {e.email}
                </option>
              ))}
          </select>
        </div>
        <button
          type="button"
          className="aiq-btn-primary"
          disabled={!selectedUserId || busy}
          onClick={handleCheck}
        >
          {busy ? "Fetching…" : "Check scores"}
        </button>
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            border: "1px solid var(--color-incorrect)",
            borderRadius: "var(--radius-sm)",
            color: "var(--color-incorrect)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {scores && (
        <div style={{ marginTop: 16 }}>
          {/* Identity row */}
          <div
            style={{
              fontSize: 13,
              color: "var(--text-secondary)",
              marginBottom: 10,
            }}
          >
            <strong style={{ color: "var(--text-primary)" }}>{scores.name || "—"}</strong>
            {" — "}
            {scores.email}
            {!scores.moodle_user_found && scores.moodle_configured && (
              <span
                className="aiq-chip"
                style={{
                  marginLeft: 8,
                  background: "rgba(234, 179, 8, 0.15)",
                  color: "#eab308",
                }}
              >
                Not in LMS
              </span>
            )}
            {!scores.moodle_configured && (
              <span
                className="aiq-chip aiq-chip-neutral"
                style={{ marginLeft: 8 }}
              >
                LMS not configured
              </span>
            )}
          </div>

          {/* 4-up score grid */}
          <div className="aiq-kpi-grid">
            <ScoreCell
              label="White Belt"
              value={scores.white}
              hint={scores.white_course || "—"}
              color={colorFor(scores.white)}
              fmtPct={fmtPct}
            />
            <ScoreCell
              label="Yellow Belt"
              value={scores.yellow}
              hint={scores.yellow_course || "—"}
              color={colorFor(scores.yellow)}
              fmtPct={fmtPct}
            />
            <ScoreCell
              label="Assessment"
              value={scores.assessment}
              hint="Best AIQ attempt"
              color={colorFor(scores.assessment)}
              fmtPct={fmtPct}
            />
            <ScoreCell
              label="Average"
              value={scores.average}
              hint="Of available scores"
              color={colorFor(scores.average)}
              fmtPct={fmtPct}
              emphasised
            />
          </div>
        </div>
      )}
    </div>
  );
}

const ScoreCell = ({ label, value, hint, color, fmtPct, emphasised }) => (
  <div
    className="aiq-kpi"
    style={emphasised ? { borderColor: color } : undefined}
  >
    <div className="aiq-kpi-label">{label}</div>
    <div className="aiq-kpi-value" style={{ color }}>
      {fmtPct(value)}
    </div>
    {hint && (
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          marginTop: 4,
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={hint}
      >
        {hint}
      </div>
    )}
  </div>
);
